import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { COPILOT_CONFIG } from './config';
import './Copilot.scss';

interface Props {
  // True when the sidebar is collapsed → show the icon only (no label).
  collapsed: boolean;
}

/**
 * Ask Clara launcher, rendered at the bottom of the left sidebar. Admin-only, gated on the
 * SAME source as CopilotPanel (useAuth().user.role) so the two never disagree. Clicking it
 * dispatches a toggle window event that CopilotPanel listens for, and it reflects the panel's
 * open/closed state (broadcast back via 'ask-clara:state'), so the sidebar stays decoupled
 * from the panel and the whole feature remains removable (delete the Copilot folder + this
 * one import in Sidebar.tsx).
 */
const AskClaraLauncher = ({ collapsed }: Props) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onState = (e: Event) => setOpen(Boolean((e as CustomEvent).detail?.open));
    window.addEventListener('ask-clara:state', onState);
    return () => window.removeEventListener('ask-clara:state', onState);
  }, []);

  if (!COPILOT_CONFIG.ENABLED || !user || !COPILOT_CONFIG.ALLOWED_ROLES.includes(user.role)) return null;

  const toggle = () => window.dispatchEvent(new CustomEvent('ask-clara:toggle'));

  return (
    <button
      type="button"
      className={`clara-sidebar-launcher${collapsed ? ' is-collapsed' : ''}${open ? ' is-open' : ''}`}
      onClick={toggle}
      title={open ? 'Close Ask Clara' : 'Ask Clara'}
      aria-label="Ask Clara"
      aria-pressed={open}
    >
      <i className={`pi ${open ? 'pi-times' : 'pi-sparkles'}`} />
      {!collapsed && <span>{open ? 'Close Clara' : 'Ask Clara'}</span>}
    </button>
  );
};

export default AskClaraLauncher;
